import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, reduce } from "../../../../../i18n";
import { EcoEfficiencyDashboardContext } from "../../../../../contexts/EcoEfficiencyDashboardContext";
import { BarSeriesType, ChartsAxisContentProps } from "@mui/x-charts";
import { MakeOptional } from "@mui/x-date-pickers/internals";
import { useEffect, useState } from "react";
import { MenuData } from "../../../../../components/input/CustomSelect";
import NumberFormatterUtils from "../../../../../utils/numberFormatterUtils";
import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid";
import { EcoEfficiencyByComponentDataGrid } from "../eco-efficiency-by-component/ecoEfficiencyByComponent.type";
import CustomEcoEfficiencyChart from "../economicEfficiency/CustomEcoEfficiencyChart";

export const ComponentPredicted = () => {
  const { t, i18n } = useTranslation([common, reduce], { nsMode: "fallback" });

  const { indicators, yearly, selectedEcoEfficiency } =
    EcoEfficiencyDashboardContext();

  const [series, setSeries] = useState<MakeOptional<BarSeriesType, "type">[]>(
    [],
  );

  const [labels, setLabels] = useState<string[]>([]);

  const columnGroup: GridColumnGroupingModel = [
    {
      groupId: t("Normalization"),
      children: [
        { field: `normalizationBaseYear` },
        { field: `normalizationPlan` },
        { field: `normalizationActual` },
      ],
      headerAlign: "center",
    },
    {
      groupId: t("Value"),
      children: [
        { field: `valueBaseYear` },
        { field: `valuePlan` },
        { field: `valueActual` },
      ],
      headerAlign: "center",
    },
  ];

  const buildTable = () => {
    if (yearly) {
      const temp = [
        ...yearly.years.map((y, i) => {
          const { year } = y;

          const component = y.components.find(
            (c) => c.economiccomponentsRef === selectedComponants,
          );

          const indicator = indicators.find(
            (indicator) =>
              indicator._id ===
              selectedEcoEfficiency?.economicindicatorsRef._id,
          );

          const componentData = indicator?.components.find(
            (c) => c._id === selectedComponants,
          );

          return {
            _id: `${year}`,
            id: `${i + 1}`,
            nameEn: `${year}`,
            nameTh: `${year}`,
            normalizationActual: component?.componentValueNormalized ?? 0,
            normalizationBaseYear:
              component?.componentValueBaseYearNormalized ?? 0,
            normalizationPlan: component?.componentValuePlanNormalized ?? 0,
            valueActual: component?.componentValue ?? 0,
            valueBaseYear: component?.componentValueBaseYear ?? 0,
            valuePlan: component?.componentValuePlan ?? 0,
            unit: componentData?.unit ?? "",
          };
        }),
      ];

      setRows(temp);
    }
  };

  const [rows, setRows] = useState<EcoEfficiencyByComponentDataGrid[]>([]);

  const columns: GridColDef<EcoEfficiencyByComponentDataGrid>[] = [
    {
      field: "id",
      headerName: t("No."),
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.row._id !== "ghg-emissions" && params.row._id !== "eco-f"
          ? `${params.id}`
          : ``;
      },
    },
    {
      field: "name",
      headerName: t("Year"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1.5,
      valueGetter: (value, row) => row.nameEn,
    },
    {
      field: "normalizationBaseYear",
      headerName: t("Base-Year"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
    },
    // {
    //   field: "normalizationPlan",
    //   headerName: t("Plan"),
    //   headerAlign: "right",
    //   align: "right",
    //   minWidth: 120,
    //   valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
    // },
    {
      field: "normalizationActual",
      headerName: t("Actual"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      renderCell: (params) => {
        return (
          <Tooltip title={params.formattedValue}>
            <Box color={"var( --status-waiting-leased-in-text)"}>
              {params.formattedValue}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "valueBaseYear",
      headerName: t("Base-Year"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
      renderCell: (params) => {
        return (
          <Tooltip title={params.formattedValue}>
            <Box>{params.row._id !== "eco-f" ? params.formattedValue : ""}</Box>
          </Tooltip>
        );
      },
    },
    // {
    //   field: "valuePlan",
    //   headerName: t("Plan"),
    //   headerAlign: "right",
    //   align: "right",
    //   minWidth: 120,
    //   valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
    //   renderCell: (params) => {
    //     return (
    //       <Tooltip title={params.formattedValue}>
    //         <Box>{params.row._id !== "eco-f" ? params.formattedValue : ""}</Box>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      field: "valueActual",
      headerName: t("Actual"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
      renderCell: (params) => {
        return (
          <Tooltip title={params.formattedValue}>
            <Box color={"var( --status-waiting-leased-in-text)"}>
              {params.row._id !== "eco-f" ? params.formattedValue : ""}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "unit",
      headerName: t("Unit"),
      headerAlign: "left",
      align: "left",
      minWidth: 140,
      flex: 1,
    },
  ];

  const [selectedComponants, setSelectedComponants] = useState<string>();

  const [filterOption, setFilterOption] = useState<MenuData[]>([]);

  useEffect(() => {
    if (!yearly || !selectedEcoEfficiency) return;

    if (
      !selectedComponants ||
      !yearly.components.some((y) => {
        return y.economiccomponentsRef === selectedComponants;
      })
    ) {
      setSelectedComponants(yearly!.components[0].economiccomponentsRef);
      return;
    }

    const years = Array.from(
      { length: 6 },
      (_, i) => selectedEcoEfficiency!.year + i,
    );
    setLabels(years.map(String)); // setLabels expects string[]

    const createValueMap = (
      key: "componentValueNormalized" | "componentValuePlanNormalized",
    ) =>
      new Map(
        yearly!.years.map(({ year, components }) => {
          const item = components.find(
            (c) => c.economiccomponentsRef === selectedComponants,
          );
          return [year, item?.[key] ?? 0];
        }),
      );

    const yearMap = createValueMap("componentValueNormalized");
    const planMap = createValueMap("componentValuePlanNormalized");

    const tempActual = years.map((y) => yearMap.get(y) ?? 0);
    const tempPlan = years.map((y) => planMap.get(y) ?? 0);

    setSeries([
      // {
      //   data: [...tempPlan],
      //   color: "#10B981",
      //   label: `${t("Plan")}`,
      // },
      {
        data: [...tempActual],
        color: "#2563EB",
        label: `${t("Actual")}`,
      },
    ]);

    setFilterOption([
      ...yearly!.components.map((c) => {
        const indicator = indicators.find((i) =>
          i.components.find((j) => j._id === c.economiccomponentsRef),
        );

        const component = indicator?.components.find(
          (i) => i._id === c.economiccomponentsRef,
        );

        return {
          nameEN: component?.nameEn ?? "",
          nameTH: component?.nameTh ?? "",
          value: component?._id ?? "",
        };
      }),
    ]);

    buildTable();
  }, [yearly, indicators, t, selectedComponants, selectedEcoEfficiency]);

  const handleCustomTooltip = (props: ChartsAxisContentProps): JSX.Element => {
    const indicator = indicators.find((i) =>
      i.components.find((j) => j._id === selectedComponants),
    );

    const years = Array.from(
      { length: 6 },
      (_, i) => selectedEcoEfficiency!.year + i,
    );

    const component = indicator?.components.find(
      (i) => i._id === selectedComponants,
    );

    // สร้าง map ของข้อมูลล่วงหน้าเพื่อเข้าถึงเร็ว
    const dataMap = new Map(
      yearly!.years.map(({ year, components }) => {
        const item = components.find(
          (c) => c.economiccomponentsRef === selectedComponants,
        );
        return [year, item];
      }),
    );

    // สร้าง tempValue โดยใช้ปีจาก `years` ที่ fix แล้ว
    const tempValue = years.map((year) => {
      const item = dataMap.get(year);
      return {
        key: String(year),
        value: item?.componentValue ?? 0,
        planValue: item?.componentValuePlan ?? 0,
      };
    });

    return CustomTooltip(
      props,
      i18n.language === "th"
        ? component?.nameTh ?? ""
        : component?.nameEn ?? "",
      component?.unit ?? "",
      tempValue,
    );
  };

  return (
    <>
      <CustomEcoEfficiencyChart
        series={series ?? []}
        labels={labels ?? []}
        header={t("Component-Planned-In-5-Years")}
        info={t("Component-Planned-In-5-Years-Description")}
        year={2024}
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroup}
        selectOption={filterOption}
        select={selectedComponants}
        setSelect={(value) => setSelectedComponants(value)}
        customTooltip={(props: ChartsAxisContentProps) =>
          handleCustomTooltip(props)
        }
      />
    </>
  );
};

const CustomTooltip = (
  props: ChartsAxisContentProps,
  select: string,
  unit: string,
  values: { key: string; value: number; planValue: number }[],
): JSX.Element => {
  const { t } = useTranslation([common, reduce], { nsMode: "fallback" });

  return (
    <Card
      sx={{
        bgcolor: "var(--white)",
        borderRadius: "8px",
        padding: "20px",
        minWidth: "320px",
      }}
    >
      <CardContent
        sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
      >
        <Box borderBottom={"1px solid var(--gray2)"}>
          <Typography variant="h3" color="secondary">
            {select}
          </Typography>
          <Typography variant="body1" color="secondary">
            {`(${unit})`}
          </Typography>
        </Box>

        <Box
          borderBottom={"1px solid var(--gray2)"}
          display={"flex"}
          flexDirection={"row"}
          gap={"20px"}
        >
          <Box flex={1.5}>
            <Typography variant="h3" color="secondary">
              {props.axisValue != null ? String(props.axisValue) : ""}
            </Typography>
          </Box>

          <Box flex={1} textAlign={"right"} minWidth={"160px"}>
            <Typography variant="h3" color="secondary">
              {`${t("Normalization")}`}
            </Typography>
          </Box>

          <Box flex={1} textAlign={"right"} minWidth={"160px"}>
            <Typography variant="h3" color="secondary">
              {`${t("Value")}`}
            </Typography>
          </Box>
        </Box>

        {props.series.map((s: any, index) => (
          <Box
            borderBottom={"1px solid var(--gray2)"}
            display={"flex"}
            flexDirection={"row"}
            key={index}
            gap={"20px"}
          >
            <Box
              key={s.color}
              display={"inline-flex"}
              gap={"8px"}
              alignItems={"center"}
              flex={1.5}
            >
              <Box
                width={"16px"}
                height={"16px"}
                bgcolor={s.color}
                borderRadius={"4px"}
              />
              <Typography variant="body1" color="secondary" align="right">
                {s.label}
              </Typography>
            </Box>

            <Box flex={1} textAlign={"right"} minWidth={"160px"}>
              <Typography variant="body1" color="secondary" align="right">
                {`${
                  isNaN(s.data[props.dataIndex ?? 0])
                    ? NumberFormatterUtils.numberFormat(0)
                    : NumberFormatterUtils.numberFormat(
                        s.data[props.dataIndex ?? 0],
                      )
                }`}
              </Typography>
            </Box>
            <Box flex={1} textAlign={"right"} minWidth={"160px"}>
              <Typography variant="body1" color="secondary" align="right">
                {`${NumberFormatterUtils.numberFormat(
                  s.label === t("Plan")
                    ? values[props.dataIndex ?? 0]?.planValue ?? 0
                    : values[props.dataIndex ?? 0]?.value ?? 0,
                )}`}
              </Typography>
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};
